import { has } from '@/commonFunctions/utils'
import { ArrowBackNoBg, CenterFocusStrong, CropFree, ErrorIcon, ZoomInMapIcon, ZoomOutMap } from '@/components/icons'
import { BRACKET_MODIFY, waterfallDiagramTemplateLink } from '@/constants/Common'
import { eventStatus } from '@/constants/TrackingConstants'
import { useAppSelector } from '@/hooks/useRedux'
import { clubSelector } from '@/pages/club/selectors'
import { CanvasWidget } from '@projectstorm/react-canvas-core'
import createEngine, { DiagramModel } from '@projectstorm/react-diagrams'
import { sortBy } from 'lodash'
import { Link } from 'react-router-dom'
import { eventSelector } from '../../selectors'
import DirectionCard from './DirectionCard'
import DefaultLinkModel from './DuprLinkModel'
import DuprNodeFactory from './DuprNodeFactory'
import DuprNodeModel from './DuprNodeModel'
import MatchNoCard from './MatchNoCard'
import PlayerNameCard from './PlayerNameCard'
import ScoreDetailsCard from './ScoreDetailsCard'
import WinnerCard from './WinnerCard'

type Props = {
  currentMatches: any
  eventFormat: any
  standings: any
  canShowStandings: any
  drawImpacted: any
}

const players = [1, 16, 8, 9, 4, 13, 5, 12, 2, 15, 7, 10, 3, 14, 6, 11]
const positions = [250, 350, 450, 550, 650, 750, 850, 950, 1050, 1150, 1250, 1350, 1450, 1550, 1650, 1750]

export const VisualDiagram = ({ currentMatches, eventFormat, standings, canShowStandings, drawImpacted }: Props) => {
  const { permissions } = useAppSelector(clubSelector)
  const { bracket } = useAppSelector(eventSelector)

  // const [openModal, setOpenModal] = useState(false)

  const round1 = currentMatches?.[0]?.matches
  const round2 = currentMatches?.[1]?.matches
  const round3 = currentMatches?.[2]?.matches
  const round4 = currentMatches?.[3]?.matches
  const slotsRound1 = round1?.map((el: any) => el.matchSlot)
  const slotsRound2 = round2?.map((el: any) => el.matchSlot)
  const slotsRound3 = round3?.map((el: any) => el.matchSlot)
  const slotsRound4 = round4?.map((el: any) => el.matchSlot)

  const downloadPdfFile = () => {
    window.open(waterfallDiagramTemplateLink, '_blank', 'noopener')
  }

  const round1MatchSlotExists = (slot: any) => {
    if (slotsRound1?.includes(slot)) {
      return true
    }
    return false
  }

  const round2MatchSlotExists = (slot: any) => {
    if (slotsRound2?.includes(slot)) {
      return true
    }
    return false
  }

  const round3MatchSlotExists = (slot: any) => {
    if (slotsRound3?.includes(slot)) {
      return true
    }
    return false
  }

  const round4MatchSlotExists = (slot: any) => {
    if (slotsRound4?.includes(slot)) {
      return true
    }
    return false
  }

  const getMatchBySlot = (slot: any) => {
    return round1?.filter((el: any) => el.matchSlot === slot)?.[0]
  }

  const getMatchBySlotRound2 = (slot: any) => {
    return round2?.filter((el: any) => el.matchSlot === slot)?.[0]
  }

  const getMatchBySlotRound3 = (slot: any) => {
    return round3?.filter((el: any) => el.matchSlot === slot)?.[0]
  }

  const getMatchBySlotRound4 = (slot: any) => {
    return round4?.filter((el: any) => el.matchSlot === slot)?.[0]
  }

  const zoomStep = 10
  const engine = createEngine()
  engine.getNodeFactories().registerFactory(new DuprNodeFactory() as any)
  const model = new DiagramModel()

  const selectTeamId = (teamId: any) => {
    const selectedNodes = model.getNodes()?.filter((el: any) => el.card.props.teamId === teamId)
    const selectedLinks = model
      .getLinks()
      ?.filter((el: any) => el.options.teamId === teamId)
      .filter((el: any) => el.sourcePort.parent.card.props.teamId === el.targetPort.parent.card.props.teamId)

    selectedNodes.forEach((el: any) => {
      el.setSelected(true)
    })

    selectedLinks.forEach((el: any) => {
      el.setSelected(true)
    })
    engine.repaintCanvas()
  }

  // 1 : North West Nodes & links //

  const northWest = new DuprNodeModel({
    card: <DirectionCard title="Northwest" />,
    width: 750,
    height: 56,
    disabled: true,
  } as any)
  northWest.setPosition(-1350, -406)

  const northWestLoosers: any = []
  const northWestMatch = round4?.find((el: any) => el.tag === 'NORTH_WEST')

  for (let i = 0; i < 1; i += 1) {
    const teamsData = sortBy(northWestMatch?.teams, 'serial')

    northWestLoosers[`n${i * 2 + 1}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            northWestMatch?.is_team_1_tbd
              ? 'TBD'
              : !northWestMatch
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    northWestLoosers[`n${i * 2 + 1}Looser`].setPosition(-950, -200)

    northWestLoosers[`n${i * 2 + 2}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            northWestMatch?.is_team_2_tbd
              ? 'TBD'
              : !northWestMatch || northWestMatch?.bye
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    northWestLoosers[`n${i * 2 + 2}Looser`].setPosition(-950, -100)
    model.addAll(northWestLoosers[`n${i * 2 + 1}Looser`], northWestLoosers[`n${i * 2 + 2}Looser`])
  }

  // n2Looser.setSelected(true);

  const checkIfWinnerNotPresent = (match: any) => {
    if (match?.bye) {
      if (match?.is_team_1_tbd) {
        return true
      }
      return false
    }
    if (match?.confirmed || match?.forfeited) {
      if (match?.is_team_1_tbd && match?.is_team_2_tbd) {
        return true
      }
      return false
    }
    return true
  }

  const northWestWinner =
    northWestMatch?.team1?.winner || northWestMatch?.bye ? northWestMatch?.team1 : northWestMatch?.team2
  const northWestWinnerNotPresent = checkIfWinnerNotPresent(northWestMatch)
  const nw1Winner = new DuprNodeModel({
    card: (
      <PlayerNameCard
        onClick={() => selectTeamId(northWestWinner?.id)}
        title={
          northWestWinnerNotPresent
            ? 'TBD'
            : eventFormat === 'SINGLES'
              ? `${northWestWinner?.player1?.fullName}`
              : `${northWestWinner?.player1?.fullName}, ${northWestWinner?.player2?.fullName}`
        }
        rank={0}
      />
    ),
    direction: 'left',
  } as any)
  nw1Winner.setPosition(-1300, -150)

  const getScoresArray = (match: any) => {
    return [
      {
        serial: 1,
        game1: match?.confirmed ? match?.team1?.game1 : -1,
        game2: match?.confirmed ? match?.team1?.game2 : -1,
        game3: match?.confirmed ? match?.team1?.game3 : -1,
        game4: match?.confirmed ? match?.team1?.game4 : -1,
        game5: match?.confirmed ? match?.team1?.game5 : -1,
      },
      {
        serial: 2,
        game1: match?.confirmed ? match?.team2?.game1 : -1,
        game2: match?.confirmed ? match?.team2?.game2 : -1,
        game3: match?.confirmed ? match?.team2?.game3 : -1,
        game4: match?.confirmed ? match?.team2?.game4 : -1,
        game5: match?.confirmed ? match?.team2?.game5 : -1,
      },
    ]
  }

  const nw1Score = new DuprNodeModel({
    card: (
      <ScoreDetailsCard
        rank={99}
        noOfGames={northWestMatch?.noOfGames}
        score={getScoresArray(northWestMatch)}
        bye={northWestMatch?.bye}
        forfeited={northWestMatch?.forfeited}
      />
    ),
    disabled: true,
  } as any)
  nw1Score.setPosition(-1300, -110)

  const nw1 = new DuprNodeModel({ card: <MatchNoCard title="NW1" />, width: 60, disabled: true } as any)
  nw1.setPosition(-960, -146)

  const n1LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: northWestMatch?.team1?.id })
  n1LooserLink.setSourcePort(northWestLoosers.n1Looser.getPort('out'))
  n1LooserLink.setTargetPort(nw1Winner.getPort('in'))

  const n2LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: northWestMatch?.team2?.id })
  n2LooserLink.setSourcePort(northWestLoosers.n2Looser.getPort('out'))
  n2LooserLink.setTargetPort(nw1Winner.getPort('in'))

  // 2 : North Nodes & Links //

  const north = new DuprNodeModel({
    card: <DirectionCard title="North" />,
    width: 1100,
    height: 56,
    disabled: true,
  } as any)
  north.setPosition(-300, -406)

  const northRound3Matches = round3?.filter((el: any) => el?.tag === 'NORTH')
  const northRound3MatchSlots = northRound3Matches?.map((el: any) => el?.matchSlot)
  const eastLooserts: any = []
  const northLooserPositions = [-300, -200, -100, 0]

  for (let i = 0; i < 2; i += 1) {
    const matchExists = northRound3MatchSlots?.includes(i + 1)
    const matchData = northRound3Matches?.filter((el: any) => el.matchSlot === i + 1)?.[0]
    const teamsData = sortBy(matchData?.teams, 'serial')

    eastLooserts[`e${i * 2 + 1}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            matchData?.is_team_1_tbd
              ? 'TBD'
              : !matchExists
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    eastLooserts[`e${i * 2 + 1}Looser`].setPosition(-250, northLooserPositions[i * 2])

    eastLooserts[`e${i * 2 + 2}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            matchData?.is_team_2_tbd
              ? 'TBD'
              : !matchExists || matchData?.bye
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    eastLooserts[`e${i * 2 + 2}Looser`].setPosition(-250, northLooserPositions[i * 2 + 1])
    model.addAll(eastLooserts[`e${i * 2 + 1}Looser`], eastLooserts[`e${i * 2 + 2}Looser`])
  }

  const northRound4Match = round4?.filter((el: any) => el?.tag === 'NORTH')?.[0]
  const round3NorthMatches = round3?.filter((el: any) => el?.tag === 'NORTH')
  const northRound4Scores: any = []
  const northRound4Winners: any = []
  const northRound4Tags: any = []

  for (let i = 0; i < 1; i += 1) {
    const teamsData = sortBy(northRound4Match?.teams, 'serial')

    northRound4Winners[`n${i * 2 + 1}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            northRound4Match?.is_team_1_tbd
              ? 'TBD'
              : !northRound4Match
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    northRound4Winners[`n${i * 2 + 1}Winner`].setPosition(100, -250)

    northRound4Winners[`n${i * 2 + 2}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            northRound4Match?.is_team_2_tbd
              ? 'TBD'
              : !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    northRound4Winners[`n${i * 2 + 2}Winner`].setPosition(100, -50)

    northRound4Scores[`n${i * 2 + 1}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          bye={round3NorthMatches?.[i * 2]?.bye}
          forfeited={round3NorthMatches?.[i * 2]?.forfeited}
          loserInfo={`Loser to NW${i + 1}`}
          noOfGames={round3NorthMatches?.[i * 2]?.noOfGames}
          score={getScoresArray(round3NorthMatches?.[i * 2])}
        />
      ),
      disabled: true,
    } as any)
    northRound4Scores[`n${i * 2 + 1}Score`].setPosition(100, -210)

    northRound4Scores[`n${i * 2 + 2}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          bye={round3NorthMatches?.[i * 2 + 1]?.bye}
          forfeited={round3NorthMatches?.[i * 2 + 1]?.forfeited}
          loserInfo={`Loser to NW${i + 1}`}
          noOfGames={round3NorthMatches?.[i * 2 + 1]?.noOfGames}
          score={getScoresArray(round3NorthMatches?.[i * 2 + 1])}
        />
      ),
      disabled: true,
    } as any)
    northRound4Scores[`n${i * 2 + 2}Score`].setPosition(100, -10)

    northRound4Tags[`n${i * 2 + 1}`] = new DuprNodeModel({
      card: <MatchNoCard title={`N${i * 2 + 1}`} />,
      width: 60,
      disabled: true,
    } as any)
    northRound4Tags[`n${i * 2 + 1}`].setPosition(0, -246)

    northRound4Tags[`n${i * 2 + 2}`] = new DuprNodeModel({
      card: <MatchNoCard title={`N${i * 2 + 2}`} />,
      width: 60,
      disabled: true,
    } as any)
    northRound4Tags[`n${i * 2 + 2}`].setPosition(0, -46)

    model.addAll(
      northRound4Winners[`n${i * 2 + 1}Winner`],
      northRound4Winners[`n${i * 2 + 2}Winner`],
      northRound4Scores[`n${i * 2 + 1}Score`],
      northRound4Scores[`n${i * 2 + 2}Score`],
      northRound4Tags[`n${i * 2 + 1}`],
      northRound4Tags[`n${i * 2 + 2}`]
    )
  }

  const northWinner =
    northRound4Match?.team1?.winner || northRound4Match?.bye ? northRound4Match?.team1 : northRound4Match?.team2
  const northWinnerNotPresent = checkIfWinnerNotPresent(northRound4Match)

  const n3Winner = new DuprNodeModel({
    card: (
      <PlayerNameCard
        onClick={() => selectTeamId(northWinner?.id)}
        title={
          northWinnerNotPresent
            ? 'TBD'
            : !northRound4Match
              ? 'N/A'
              : eventFormat === 'SINGLES'
                ? `${northWinner?.player1?.fullName}`
                : `${northWinner?.player1?.fullName}, ${northWinner?.player2?.fullName}`
        }
        rank={0}
      />
    ),
  } as any)
  n3Winner.setPosition(450, -150)

  const n3Score = new DuprNodeModel({
    card: (
      <ScoreDetailsCard
        rank={99}
        noOfGames={northRound4Match?.noOfGames}
        score={getScoresArray(northRound4Match)}
        bye={northRound4Match?.bye}
        forfeited={northRound4Match?.forfeited}
      />
    ),
    disabled: true,
  } as any)
  n3Score.setPosition(450, -110)

  const n3 = new DuprNodeModel({ card: <MatchNoCard title="N3" />, width: 60, disabled: true } as any)
  n3.setPosition(350, -146)

  const e1LooserLink = new DefaultLinkModel({ teamId: round3NorthMatches?.[0]?.team1?.id })
  e1LooserLink.setSourcePort(eastLooserts.e1Looser.getPort('out'))
  e1LooserLink.setTargetPort(northRound4Winners.n1Winner.getPort('in'))

  const e2LooserLink = new DefaultLinkModel({ teamId: round3NorthMatches?.[0]?.team2?.id })
  e2LooserLink.setSourcePort(eastLooserts.e2Looser.getPort('out'))
  e2LooserLink.setTargetPort(northRound4Winners.n1Winner.getPort('in'))

  const e3LooserLink = new DefaultLinkModel({ teamId: round3NorthMatches?.[1]?.team1?.id })
  e3LooserLink.setSourcePort(eastLooserts.e3Looser.getPort('out'))
  e3LooserLink.setTargetPort(northRound4Winners.n2Winner.getPort('in'))

  const e4LooserLink = new DefaultLinkModel({ teamId: round3NorthMatches?.[1]?.team2?.id })
  e4LooserLink.setSourcePort(eastLooserts.e4Looser.getPort('out'))
  e4LooserLink.setTargetPort(northRound4Winners.n2Winner.getPort('in'))

  const n1WinnerLink = new DefaultLinkModel({ teamId: northRound4Match?.team1.id })
  n1WinnerLink.setSourcePort(northRound4Winners.n1Winner.getPort('out'))
  n1WinnerLink.setTargetPort(n3Winner.getPort('in'))

  const n2WinnerLink = new DefaultLinkModel({ teamId: northRound4Match?.team2.id })
  n2WinnerLink.setSourcePort(northRound4Winners.n2Winner.getPort('out'))
  n2WinnerLink.setTargetPort(n3Winner.getPort('in'))

  // 3: North East Nodes & Links //

  const northEast = new DuprNodeModel({
    card: <DirectionCard title="Northeast" />,
    width: 750,
    height: 56,
    disabled: true,
  } as any)
  northEast.setPosition(1100, -406)

  const northEastRound4Match = round4?.filter((el: any) => el?.tag === 'NORTH_EAST')?.[0]
  const eastRound3Loosers: any = []

  for (let i = 0; i < 1; i += 1) {
    const teamsData = sortBy(northEastRound4Match?.teams, 'serial')
    eastRound3Loosers[`e${i * 2 + 5}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            northEastRound4Match?.is_team_1_tbd
              ? 'TBD'
              : !northRound4Match
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    eastRound3Loosers[`e${i * 2 + 5}Looser`].setPosition(1150, -200)

    eastRound3Loosers[`e${i * 2 + 6}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            northEastRound4Match?.is_team_2_tbd
              ? 'TBD'
              : !northRound4Match
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    eastRound3Loosers[`e${i * 2 + 6}Looser`].setPosition(1150, -100)
    model.addAll(eastRound3Loosers[`e${i * 2 + 5}Looser`], eastRound3Loosers[`e${i * 2 + 6}Looser`])
  }

  const northEastWinner =
    northEastRound4Match?.team1?.winner || northEastRound4Match?.bye
      ? northEastRound4Match?.team1
      : northEastRound4Match?.team2
  const northEastWinnerNotPresent = checkIfWinnerNotPresent(northEastRound4Match)

  const ne1Winner = new DuprNodeModel({
    card: (
      <PlayerNameCard
        winner={!northEastWinnerNotPresent}
        onClick={() => selectTeamId(northEastWinner?.id)}
        title={
          northEastWinnerNotPresent
            ? 'TBD'
            : !northRound4Match
              ? 'N/A'
              : eventFormat === 'SINGLES'
                ? `${northEastWinner?.player1?.fullName}`
                : `${northEastWinner?.player1?.fullName}, ${northEastWinner?.player2?.fullName}`
        }
      />
    ),
  } as any)
  ne1Winner.setPosition(1500, -150)

  const ne1Score = new DuprNodeModel({
    card: (
      <ScoreDetailsCard
        rank={3}
        noOfGames={northEastRound4Match?.noOfGames}
        score={getScoresArray(northEastRound4Match)}
        bye={northEastRound4Match?.bye}
        forfeited={northEastRound4Match?.forfeited}
      />
    ),
    disabled: true,
  } as any)
  ne1Score.setPosition(1500, -110)

  const ne1 = new DuprNodeModel({ card: <MatchNoCard title="NE1" />, width: 60, disabled: true } as any)
  ne1.setPosition(1400, -146)

  const e5LooserLink = new DefaultLinkModel({ teamId: northEastRound4Match?.team1?.id })
  e5LooserLink.setSourcePort(eastRound3Loosers.e5Looser.getPort('out'))
  e5LooserLink.setTargetPort(ne1Winner.getPort('in'))

  const e6LooserLink = new DefaultLinkModel({ teamId: northEastRound4Match?.team2?.id })
  e6LooserLink.setSourcePort(eastRound3Loosers.e6Looser.getPort('out'))
  e6LooserLink.setTargetPort(ne1Winner.getPort('in'))

  // 4: South West Nodes & Links //

  const southWest = new DuprNodeModel({
    card: <DirectionCard title="Southwest" />,
    width: 750,
    height: 56,
    disabled: true,
  } as any)
  southWest.setPosition(-1350, 1850)

  const westRoud3Loosers: any = []
  const southWestRound4Match = round4?.find((el: any) => el.tag === 'SOUTH_WEST')
  for (let i = 0; i < 1; i += 1) {
    const teamsData = sortBy(southWestRound4Match?.teams, 'serial')
    westRoud3Loosers[`w${i * 2 + 5}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            southWestRound4Match?.is_team_1_tbd
              ? 'TBD'
              : !teamsData?.[0]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    westRoud3Loosers[`w${i * 2 + 5}Looser`].setPosition(-950, 2050)

    westRoud3Loosers[`w${i * 2 + 6}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            southWestRound4Match?.is_team_2_tbd
              ? 'TBD'
              : !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    westRoud3Loosers[`w${i * 2 + 6}Looser`].setPosition(-950, 2150)
    model.addAll(westRoud3Loosers[`w${i * 2 + 5}Looser`], westRoud3Loosers[`w${i * 2 + 6}Looser`])
  }

  const sw1WinnerTeam =
    southWestRound4Match?.team1?.winner || (southWestRound4Match?.bye && southWestRound4Match?.team1?.id)
      ? southWestRound4Match?.team1
      : southWestRound4Match?.team2
  const sw1WinnerNotPresent = checkIfWinnerNotPresent(southWestRound4Match)
  const sw1Winner = new DuprNodeModel({
    card: (
      <PlayerNameCard
        onClick={() => selectTeamId(sw1WinnerTeam?.id)}
        title={
          sw1WinnerNotPresent
            ? 'TBD'
            : !sw1WinnerTeam
              ? 'N/A'
              : eventFormat === 'SINGLES'
                ? `${sw1WinnerTeam?.player1?.fullName}`
                : `${sw1WinnerTeam?.player1?.fullName}, ${sw1WinnerTeam?.player2?.fullName}`
        }
        rank={0}
      />
    ),
    direction: 'left',
  } as any)
  sw1Winner.setPosition(-1300, 2100)

  const sw1Score = new DuprNodeModel({
    card: (
      <ScoreDetailsCard
        rank={99}
        noOfGames={southWestRound4Match?.noOfGames}
        score={getScoresArray(southWestRound4Match)}
        bye={southWestRound4Match?.bye}
        forfeited={southWestRound4Match?.forfeited}
      />
    ),
    disabled: true,
  } as any)
  sw1Score.setPosition(-1300, 2140)

  const sw1 = new DuprNodeModel({ card: <MatchNoCard title="SW1" />, width: 60, disabled: true } as any)
  sw1.setPosition(-960, 2106)

  const w5LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: southWestRound4Match?.team1?.id })
  w5LooserLink.setSourcePort(westRoud3Loosers.w5Looser.getPort('out'))
  w5LooserLink.setTargetPort(sw1Winner.getPort('in'))

  const w6LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: southWestRound4Match?.team2?.id })
  w6LooserLink.setSourcePort(westRoud3Loosers.w6Looser.getPort('out'))
  w6LooserLink.setTargetPort(sw1Winner.getPort('in'))

  // 5: South Nodes & Links //

  const south = new DuprNodeModel({
    card: <DirectionCard title="South" />,
    width: 1100,
    height: 56,
    disabled: true,
  } as any)
  south.setPosition(-300, 1850)

  const southRound3Matches = round3?.filter((el: any) => el?.tag === 'SOUTH')
  const soutRound3MatchSlots = southRound3Matches?.map((el: any) => el?.matchSlot)
  const westLoosers: any = []
  const westLooserPositions = [1950, 2050, 2150, 2250]

  for (let i = 0; i < 2; i += 1) {
    const matchExists = soutRound3MatchSlots?.includes(i + 1)
    const matchData = southRound3Matches?.filter((el: any) => el.matchSlot === i + 1)?.[0]
    const teamsData = sortBy(matchData?.teams, 'serial')

    westLoosers[`w${i * 2 + 1}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            matchData?.is_team_1_tbd
              ? 'TBD'
              : !matchExists || !teamsData?.[0]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    westLoosers[`w${i * 2 + 1}Looser`].setPosition(450, westLooserPositions[i * 2])

    westLoosers[`w${i * 2 + 2}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            matchData?.is_team_2_tbd
              ? 'TBD'
              : !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    westLoosers[`w${i * 2 + 2}Looser`].setPosition(450, westLooserPositions[i * 2 + 1])
    model.addAll(westLoosers[`w${i * 2 + 1}Looser`], westLoosers[`w${i * 2 + 2}Looser`])
  }

  const southRound4Match = round4?.find((el: any) => el?.tag === 'SOUTH')
  const southRound4Scores: any = []
  const southRound4Winners: any = []
  const southRound4Tags: any = []

  for (let i = 0; i < 1; i += 1) {
    const teamsData = sortBy(southRound4Match?.teams, 'teams')
    southRound4Winners[`s${i * 2 + 1}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            southRound4Match?.is_team_1_tbd
              ? 'TBD'
              : !southRound4Match
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    southRound4Winners[`s${i * 2 + 1}Winner`].setPosition(100, 2000)

    southRound4Winners[`s${i * 2 + 2}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            southRound4Match?.is_team_2_tbd
              ? 'TBD'
              : !southRound4Match || !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    southRound4Winners[`s${i * 2 + 2}Winner`].setPosition(100, 2200)

    southRound4Scores[`s${i * 2 + 1}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          bye={southRound3Matches?.[i * 2]?.bye}
          forfeited={southRound3Matches?.[i * 2]?.forfeited}
          loserInfo={`Loser to SE${i + 1}`}
          noOfGames={southRound3Matches?.[i * 2]?.noOfGames}
          score={getScoresArray(southRound3Matches?.[i * 2])}
        />
      ),
      disabled: true,
    } as any)
    southRound4Scores[`s${i * 2 + 1}Score`].setPosition(100, 2040)

    southRound4Scores[`s${i * 2 + 2}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          bye={southRound3Matches?.[i * 2 + 1]?.bye}
          forfeited={southRound3Matches?.[i * 2 + 1]?.forfeited}
          loserInfo={`Loser to SE${i + 1}`}
          noOfGames={southRound3Matches?.[i * 2 + 1]?.noOfGames}
          score={getScoresArray(southRound3Matches?.[i * 2 + 1])}
        />
      ),
      disabled: true,
    } as any)
    southRound4Scores[`s${i * 2 + 2}Score`].setPosition(100, 2240)

    southRound4Tags[`s${i * 2 + 1}`] = new DuprNodeModel({
      card: <MatchNoCard title={`S${i * 1 + 1}`} />,
      width: 60,
      disabled: true,
    } as any)
    southRound4Tags[`s${i * 2 + 1}`].setPosition(440, 2004)

    southRound4Tags[`s${i * 2 + 2}`] = new DuprNodeModel({
      card: <MatchNoCard title={`S${i * 1 + 2}`} />,
      width: 60,
      disabled: true,
    } as any)
    southRound4Tags[`s${i * 2 + 2}`].setPosition(440, 2204)

    model.addAll(
      southRound4Winners[`s${i * 2 + 1}Winner`],
      southRound4Winners[`s${i * 2 + 2}Winner`],
      southRound4Scores[`s${i * 2 + 1}Score`],
      southRound4Scores[`s${i * 2 + 2}Score`],
      southRound4Tags[`s${i * 2 + 1}`],
      southRound4Tags[`s${i * 2 + 2}`]
    )
  }

  const southWinner =
    southRound4Match?.team1?.winner || southRound4Match?.bye ? southRound4Match?.team1 : southRound4Match?.team2
  const southWinnerNotPresent = checkIfWinnerNotPresent(southRound4Match)

  const s3Winner = new DuprNodeModel({
    card: (
      <PlayerNameCard
        onClick={() => selectTeamId(southWinner?.id)}
        title={
          southWinnerNotPresent
            ? 'TBD'
            : !southRound4Match
              ? 'N/A'
              : eventFormat === 'SINGLES'
                ? `${southWinner?.player1?.fullName}`
                : `${southWinner?.player1?.fullName}, ${southWinner?.player2?.fullName}`
        }
        rank={0}
      />
    ),
    direction: 'left',
  } as any)
  s3Winner.setPosition(-250, 2100)

  const s3Score = new DuprNodeModel({
    card: (
      <ScoreDetailsCard
        rank={99}
        noOfGames={southRound4Match?.noOfGames}
        score={getScoresArray(southRound4Match)}
        bye={southRound4Match?.bye}
        forfeited={southRound4Match?.forfeited}
      />
    ),
    disabled: true,
  } as any)
  s3Score.setPosition(-250, 2140)

  const s3 = new DuprNodeModel({ card: <MatchNoCard title="S3" />, width: 60, disabled: true } as any)
  s3.setPosition(90, 2104)

  const w1LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: southRound3Matches?.[0]?.team1?.id })
  w1LooserLink.setSourcePort(westLoosers.w1Looser.getPort('out'))
  w1LooserLink.setTargetPort(southRound4Winners.s1Winner.getPort('in'))

  const w2LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: southRound3Matches?.[0]?.team2?.id })
  w2LooserLink.setSourcePort(westLoosers.w2Looser.getPort('out'))
  w2LooserLink.setTargetPort(southRound4Winners.s1Winner.getPort('in'))

  const w3LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: southRound3Matches?.[1]?.team1?.id })
  w3LooserLink.setSourcePort(westLoosers.w3Looser.getPort('out'))
  w3LooserLink.setTargetPort(southRound4Winners.s2Winner.getPort('in'))

  const w4LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: southRound3Matches?.[1]?.team2?.id })
  w4LooserLink.setSourcePort(westLoosers.w4Looser.getPort('out'))
  w4LooserLink.setTargetPort(southRound4Winners.s2Winner.getPort('in'))

  const s1WinnerLink = new DefaultLinkModel({ curvyness: -50, teamId: southRound4Match?.team1?.id })
  s1WinnerLink.setSourcePort(southRound4Winners.s1Winner.getPort('out'))
  s1WinnerLink.setTargetPort(s3Winner.getPort('in'))

  const s2WinnerLink = new DefaultLinkModel({ curvyness: -50, teamId: southRound4Match?.team2?.id })
  s2WinnerLink.setSourcePort(southRound4Winners.s2Winner.getPort('out'))
  s2WinnerLink.setTargetPort(s3Winner.getPort('in'))

  // 6: South East Nodes & Links //

  const southEast = new DuprNodeModel({
    card: <DirectionCard title="Southeast" />,
    width: 750,
    height: 56,
    disabled: true,
  } as any)
  southEast.setPosition(1100, 1850)

  const southRoud3Loosers: any = []
  const southEastRound4Match = round4?.find((el: any) => el.tag === 'SOUTH_EAST')
  for (let i = 0; i < 1; i += 1) {
    const teamsData = sortBy(southEastRound4Match?.teams, 'serial')

    southRoud3Loosers[`s${i * 2 + 1}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            southEastRound4Match?.is_team_1_tbd
              ? 'TBD'
              : !southEastRound4Match
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    southRoud3Loosers[`s${i * 2 + 1}Looser`].setPosition(1150, 2050)

    southRoud3Loosers[`s${i * 2 + 2}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            southEastRound4Match?.is_team_2_tbd
              ? 'TBD'
              : !southEastRound4Match || !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    southRoud3Loosers[`s${i * 2 + 2}Looser`].setPosition(1150, 2150)
    model.addAll(southRoud3Loosers[`s${i * 2 + 1}Looser`], southRoud3Loosers[`s${i * 2 + 2}Looser`])
  }

  const southEastWinnerTeam =
    southEastRound4Match?.team1?.winner || southEastRound4Match?.bye
      ? southEastRound4Match?.team1
      : southEastRound4Match?.team2
  const southEastWinnerNotPresent = checkIfWinnerNotPresent(southEastRound4Match)

  const se1Winner = new DuprNodeModel({
    card: (
      <PlayerNameCard
        onClick={() => selectTeamId(southEastWinnerTeam?.id)}
        title={
          southEastWinnerNotPresent
            ? 'TBD'
            : !southEastWinnerTeam
              ? 'N/A'
              : eventFormat === 'SINGLES'
                ? `${southEastWinnerTeam?.player1?.fullName}`
                : `${southEastWinnerTeam?.player1?.fullName}, ${southEastWinnerTeam?.player2?.fullName}`
        }
        rank={0}
      />
    ),
  } as any)
  se1Winner.setPosition(1500, 2100)

  const se1Score = new DuprNodeModel({
    card: (
      <ScoreDetailsCard
        rank={99}
        noOfGames={southEastRound4Match?.noOfGames}
        score={getScoresArray(southEastRound4Match)}
        bye={southEastRound4Match?.bye}
        forfeited={southEastRound4Match?.forfeited}
      />
    ),
    disabled: true,
  } as any)
  se1Score.setPosition(1500, 2140)

  const se1 = new DuprNodeModel({ card: <MatchNoCard title="SE1" />, width: 60, disabled: true } as any)
  se1.setPosition(1400, 2106)

  const s1LooserLink = new DefaultLinkModel({ teamId: southEastRound4Match?.team1?.id })
  s1LooserLink.setSourcePort(southRoud3Loosers.s1Looser.getPort('out'))
  s1LooserLink.setTargetPort(se1Winner.getPort('in'))

  const s2LooserLink = new DefaultLinkModel({ teamId: southEastRound4Match?.team2?.id })
  s2LooserLink.setSourcePort(southRoud3Loosers.s2Looser.getPort('out'))
  s2LooserLink.setTargetPort(se1Winner.getPort('in'))

  // 7: West Nodes & Links //

  const west = new DuprNodeModel({
    card: <DirectionCard title="West" />,
    width: 1400,
    height: 56,
    disabled: true,
  } as any)
  west.setPosition(-1350, 144)

  const middleLoosers: any = []
  const middleLoosersPosition = [300, 500, 700, 900, 1100, 1300, 1500, 1700]
  const westLooserTeams = round2?.filter((el: any) => el.tag === 'WEST')

  const getWestMatchRound2BySlot = (slot: any) => {
    return westLooserTeams?.filter((el: any) => el.matchSlot === slot)?.[0]
  }

  for (let i = 0; i < 4; i += 1) {
    const match2 = round2MatchSlotExists(i + 1)
    const matchData = getWestMatchRound2BySlot(i + 1)
    const teamsData = sortBy(matchData?.teams, 'serial')

    middleLoosers[`m${i * 2 + 1}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            matchData?.is_team_1_tbd
              ? 'TBD'
              : !match2 || !teamsData?.[0]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    middleLoosers[`m${i * 2 + 1}Looser`].setPosition(-250, middleLoosersPosition[i * 2])

    middleLoosers[`m${i * 2 + 2}Looser`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            matchData?.is_team_2_tbd
              ? 'TBD'
              : !teamsData?.[1]?.player1?.fullName || !match2
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    middleLoosers[`m${i * 2 + 2}Looser`].setPosition(-250, middleLoosersPosition[i * 2 + 1])
    model.addAll(middleLoosers[`m${i * 2 + 1}Looser`], middleLoosers[`m${i * 2 + 2}Looser`])
  }

  const round3WestWinners: any = []
  const round3WestScores: any = []
  const round3WestTags: any = []

  const round3WestWinnerPositions = [400, 800, 1200, 1600]
  const round3WestScoresPositions = [440, 840, 1240, 1640]
  const round3WestTagsPositions = [404, 804, 1204, 1604]

  const westRound3Teams = round3?.filter((el: any) => el.tag === 'WEST')

  const getWestMatchRound3BySlot = (slot: any) => {
    return westRound3Teams?.filter((el: any) => el.matchSlot === slot)?.[0]
  }

  const round2WestMAtches = round2?.filter((el: any) => el.tag === 'WEST')
  for (let i = 0; i < 2; i += 1) {
    const match3 = round3MatchSlotExists(i + 1)
    const matchData = getWestMatchRound3BySlot(i + 1)
    const teamsData = sortBy(matchData?.teams, 'serial')

    round3WestWinners[`w${i * 2 + 1}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            matchData?.is_team_1_tbd
              ? 'TBD'
              : !match3 || !teamsData?.[0]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    round3WestWinners[`w${i * 2 + 1}Winner`].setPosition(-600, round3WestWinnerPositions[i * 2])

    round3WestWinners[`w${i * 2 + 2}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            matchData?.is_team_2_tbd
              ? 'TBD'
              : !match3 || !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    round3WestWinners[`w${i * 2 + 2}Winner`].setPosition(-600, round3WestWinnerPositions[i * 2 + 1])

    round3WestScores[`w${i * 2 + 1}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          loserInfo={`Loser to S${i + 1}`}
          noOfGames={round2WestMAtches?.[i * 2]?.noOfGames}
          bye={round2WestMAtches?.[i * 2]?.bye}
          forfeited={round2WestMAtches?.[i * 2]?.forfeited}
          score={getScoresArray(round2WestMAtches?.[i * 2])}
        />
      ),
      disabled: true,
    } as any)
    round3WestScores[`w${i * 2 + 1}Score`].setPosition(-600, round3WestScoresPositions[i * 2])

    round3WestScores[`w${i * 2 + 2}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          loserInfo={`Loser to S${i + 1}`}
          noOfGames={round2WestMAtches?.[i * 2 + 1]?.noOfGames}
          bye={round2WestMAtches?.[i * 2 + 1]?.bye}
          forfeited={round2WestMAtches?.[i * 2 + 1]?.forfeited}
          score={getScoresArray(round2WestMAtches?.[i * 2 + 1])}
        />
      ),
      disabled: true,
    } as any)
    round3WestScores[`w${i * 2 + 2}Score`].setPosition(-600, round3WestScoresPositions[i * 2 + 1])

    round3WestTags[`w${i * 2 + 1}`] = new DuprNodeModel({
      card: <MatchNoCard title={`W${i * 2 + 1}`} />,
      width: 60,
      disabled: true,
    } as any)
    round3WestTags[`w${i * 2 + 1}`].setPosition(-260, round3WestTagsPositions[i * 2])

    round3WestTags[`w${i * 2 + 2}`] = new DuprNodeModel({
      card: <MatchNoCard title={`W${i * 2 + 2}`} />,
      width: 60,
      disabled: true,
    } as any)
    round3WestTags[`w${i * 2 + 2}`].setPosition(-260, round3WestTagsPositions[i * 2 + 1])
    model.addAll(
      round3WestWinners[`w${i * 2 + 1}Winner`],
      round3WestWinners[`w${i * 2 + 2}Winner`],
      round3WestScores[`w${i * 2 + 1}Score`],
      round3WestScores[`w${i * 2 + 2}Score`],
      round3WestTags[`w${i * 2 + 1}`],
      round3WestTags[`w${i * 2 + 2}`]
    )
  }

  const round4WestWinners: any = []
  const round4WestScores: any = []
  const round4WestTags: any = []
  const round3WestMatches = round3?.filter((el: any) => el.tag === 'WEST')

  for (let i = 0; i < 1; i += 1) {
    const match4 = round4?.filter((el: any) => el.tag === 'WEST')?.[0]
    const matchData = round4?.filter((el: any) => el.tag === 'WEST')?.[0]
    const teamsData = sortBy(matchData?.teams, 'serial')

    round4WestWinners.w5Winner = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            matchData?.is_team_1_tbd
              ? 'TBD'
              : !match4
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    round4WestWinners.w5Winner.setPosition(-950, 600)

    round4WestWinners.w6Winner = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            matchData?.is_team_2_tbd
              ? 'TBD'
              : !match4 || !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
      direction: 'left',
    } as any)
    round4WestWinners.w6Winner.setPosition(-950, 1400)

    round4WestScores.w5Score = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          loserInfo="Loser to SW1"
          noOfGames={round3WestMatches?.[i * 2]?.noOfGames}
          bye={round3WestMatches?.[i * 2]?.bye}
          forfeited={round3WestMatches?.[i * 2]?.forfeited}
          score={getScoresArray(round3WestMatches?.[i * 2])}
        />
      ),
      disabled: true,
    } as any)
    round4WestScores.w5Score.setPosition(-950, 640)

    round4WestScores.w6Score = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          loserInfo="Loser to SW1"
          noOfGames={round3WestMatches?.[i * 2 + 1]?.noOfGames}
          bye={round3WestMatches?.[i * 2 + 1]?.bye}
          forfeited={round3WestMatches?.[i * 2 + 1]?.forfeited}
          score={getScoresArray(round3WestMatches?.[i * 2 + 1])}
        />
      ),
      disabled: true,
    } as any)
    round4WestScores.w6Score.setPosition(-950, 1440)

    round4WestTags.w5 = new DuprNodeModel({ card: <MatchNoCard title="W5" />, width: 60, disabled: true } as any)
    round4WestTags.w5.setPosition(-610, 604)

    round4WestTags.w6 = new DuprNodeModel({ card: <MatchNoCard title="W6" />, width: 60, disabled: true } as any)
    round4WestTags.w6.setPosition(-610, 1404)
    model.addAll(
      round4WestWinners.w5Winner,
      round4WestWinners.w6Winner,
      round4WestScores.w5Score,
      round4WestScores.w6Score,
      round4WestTags.w5,
      round4WestTags.w6
    )
  }

  const westFinalMatch = round4?.filter((el: any) => el.tag === 'WEST')?.[0]
  const westWinner =
    westFinalMatch?.team1?.winner || westFinalMatch?.bye ? westFinalMatch?.team1 : westFinalMatch?.team2
  const westWinnerNotPresent = checkIfWinnerNotPresent(westFinalMatch)

  const w7Winner = new DuprNodeModel({
    card: (
      <PlayerNameCard
        onClick={() => selectTeamId(westWinner?.id)}
        title={
          westWinnerNotPresent
            ? 'TBD'
            : eventFormat === 'SINGLES'
              ? `${westWinner?.player1?.fullName}`
              : `${westWinner?.player1?.fullName}, ${westWinner?.player2?.fullName}`
        }
        rank={0}
      />
    ),
    direction: 'left',
  } as any)
  w7Winner.setPosition(-1300, 1000)

  const w7Score = new DuprNodeModel({
    card: (
      <ScoreDetailsCard
        rank={99}
        noOfGames={westFinalMatch?.noOfGames}
        score={getScoresArray(westFinalMatch)}
        bye={westFinalMatch?.bye}
        forfeited={westFinalMatch?.forfeited}
      />
    ),
    disabled: true,
  } as any)
  w7Score.setPosition(-1300, 1040)

  const w7 = new DuprNodeModel({ card: <MatchNoCard title="W7" />, width: 60, disabled: true } as any)
  w7.setPosition(-960, 1004)

  const m1Team = getWestMatchRound2BySlot(1)
  const m2Team = getWestMatchRound2BySlot(2)
  const m3Team = getWestMatchRound2BySlot(3)
  const m4Team = getWestMatchRound2BySlot(4)
  // const m5Team = getMatchBySlot(5);
  // const m6Team = getMatchBySlot(6);
  // const m7Team = getMatchBySlot(7);
  // const m8Team = getMatchBySlot(8);

  // const m1LooserTeamId = !m1Team?.team1?.winner ? m1Team?.team1?.id : m1Team?.team2?.id;
  // const m2LooserTeamId = !m2Team?.team1?.winner ? m2Team?.team1?.id : m2Team?.team2?.id;
  // const m3LooserTeamId = !m3Team?.team1?.winner ? m3Team?.team1?.id : m3Team?.team2?.id;
  // const m4LooserTeamId = !m4Team?.team1?.winner ? m4Team?.team1?.id : m4Team?.team2?.id;
  // const m5LooserTeamId = !m5Team?.team1?.winner ? m5Team?.team1?.id : m5Team?.team2?.id;
  // const m6LooserTeamId = !m6Team?.team1?.winner ? m6Team?.team1?.id : m6Team?.team2?.id;
  // const m7LooserTeamId = !m7Team?.team1?.winner ? m7Team?.team1?.id : m7Team?.team2?.id;
  // const m8LooserTeamId = !m8Team?.team1?.winner ? m8Team?.team1?.id : m8Team?.team2?.id;

  const m1LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: m1Team?.team1?.id })
  m1LooserLink.setSourcePort(middleLoosers.m1Looser.getPort('out'))
  m1LooserLink.setTargetPort(round3WestWinners.w1Winner.getPort('in'))

  const m2LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: m1Team?.team2?.id })
  m2LooserLink.setSourcePort(middleLoosers.m2Looser.getPort('out'))
  m2LooserLink.setTargetPort(round3WestWinners.w1Winner.getPort('in'))

  const m3LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: m2Team?.team1?.id })
  m3LooserLink.setSourcePort(middleLoosers.m3Looser.getPort('out'))
  m3LooserLink.setTargetPort(round3WestWinners.w2Winner.getPort('in'))

  const m4LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: m2Team?.team2?.id })
  m4LooserLink.setSourcePort(middleLoosers.m4Looser.getPort('out'))
  m4LooserLink.setTargetPort(round3WestWinners.w2Winner.getPort('in'))

  const m5LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: m3Team?.team1?.id })
  m5LooserLink.setSourcePort(middleLoosers.m5Looser.getPort('out'))
  m5LooserLink.setTargetPort(round3WestWinners.w3Winner.getPort('in'))

  const m6LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: m3Team?.team2?.id })
  m6LooserLink.setSourcePort(middleLoosers.m6Looser.getPort('out'))
  m6LooserLink.setTargetPort(round3WestWinners.w3Winner.getPort('in'))

  const m7LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: m4Team?.team1?.id })
  m7LooserLink.setSourcePort(middleLoosers.m7Looser.getPort('out'))
  m7LooserLink.setTargetPort(round3WestWinners.w4Winner.getPort('in'))

  const m8LooserLink = new DefaultLinkModel({ curvyness: -50, teamId: m4Team?.team2?.id })
  m8LooserLink.setSourcePort(middleLoosers.m8Looser.getPort('out'))
  m8LooserLink.setTargetPort(round3WestWinners.w4Winner.getPort('in'))

  const w5Team = getWestMatchRound3BySlot(1)
  const w6Team = getWestMatchRound3BySlot(2)

  const w1WinnerLink = new DefaultLinkModel({ curvyness: -50, teamId: w5Team?.team1?.id })
  w1WinnerLink.setSourcePort(round3WestWinners.w1Winner.getPort('out'))
  w1WinnerLink.setTargetPort(round4WestWinners.w5Winner.getPort('in'))

  const w2WinnerLink = new DefaultLinkModel({ curvyness: -50, teamId: w5Team?.team2?.id })
  w2WinnerLink.setSourcePort(round3WestWinners.w2Winner.getPort('out'))
  w2WinnerLink.setTargetPort(round4WestWinners.w5Winner.getPort('in'))

  const w3WinnerLink = new DefaultLinkModel({ curvyness: -50, teamId: w6Team?.team1?.id })
  w3WinnerLink.setSourcePort(round3WestWinners.w3Winner.getPort('out'))
  w3WinnerLink.setTargetPort(round4WestWinners.w6Winner.getPort('in'))

  const w4WinnerLink = new DefaultLinkModel({ curvyness: -50, teamId: w6Team?.team2?.id })
  w4WinnerLink.setSourcePort(round3WestWinners.w4Winner.getPort('out'))
  w4WinnerLink.setTargetPort(round4WestWinners.w6Winner.getPort('in'))

  const w5WinnerLink = new DefaultLinkModel({ curvyness: -50, teamId: westFinalMatch?.team1?.id })
  w5WinnerLink.setSourcePort(round4WestWinners.w5Winner.getPort('out'))
  w5WinnerLink.setTargetPort(w7Winner.getPort('in'))

  const w6WinnerLink = new DefaultLinkModel({ curvyness: -50, teamId: westFinalMatch?.team2?.id })
  w6WinnerLink.setSourcePort(round4WestWinners.w6Winner.getPort('out'))
  w6WinnerLink.setTargetPort(w7Winner.getPort('in'))

  // 8: East nodes & Links //

  const east = new DuprNodeModel({
    card: <DirectionCard title="East" />,
    width: 1400,
    height: 56,
    disabled: true,
  } as any)
  east.setPosition(450, 144)

  const middleWinners: any = []
  const middleScores: any = []
  const middleTags: any = []
  const middleWinnerPositions = [300, 500, 700, 900, 1100, 1300, 1500, 1700]
  const middleScoresPositions = [340, 540, 740, 940, 1140, 1340, 1540, 1740]
  const middleTagsPositions = [304, 504, 704, 904, 1104, 1304, 1504, 1704]
  for (let i = 0; i < 4; i += 1) {
    const match2 = round2MatchSlotExists(i + 1)
    const matchData = getMatchBySlotRound2(i + 1)
    const teamsData = sortBy(matchData?.teams, 'serial')

    middleWinners[`m${i * 2 + 1}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            matchData?.is_team_1_tbd
              ? 'TBD'
              : !match2 || !teamsData?.[0]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    middleWinners[`m${i * 2 + 1}Winner`].setPosition(450, middleWinnerPositions[i * 2])

    middleWinners[`m${i * 2 + 2}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            matchData?.is_team_2_tbd
              ? 'TBD'
              : !match2 || !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    middleWinners[`m${i * 2 + 2}Winner`].setPosition(450, middleWinnerPositions[i * 2 + 1])

    middleScores[`m${i * 2 + 1}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          loserInfo={`Loser to W${i + 1}`}
          noOfGames={round1?.[i * 2]?.noOfGames}
          bye={round1?.[i * 2]?.bye}
          forfeited={round1?.[i * 2]?.forfeited}
          score={getScoresArray(round1?.[i * 2])}
        />
      ),
      disabled: true,
    } as any)
    middleScores[`m${i * 2 + 1}Score`].setPosition(450, middleScoresPositions[i * 2])

    middleScores[`m${i * 2 + 2}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          loserInfo={`Loser to W${i + 1}`}
          noOfGames={round1?.[i * 2 + 1]?.noOfGames}
          bye={round1?.[i * 2 + 1]?.bye}
          forfeited={round1?.[i * 2 + 1]?.forfeited}
          score={getScoresArray(round1?.[i * 2 + 1])}
        />
      ),
      disabled: true,
    } as any)
    middleScores[`m${i * 2 + 2}Score`].setPosition(450, middleScoresPositions[i * 2 + 1])

    middleTags[`m${i * 2 + 1}`] = new DuprNodeModel({
      card: <MatchNoCard title={`M${i * 2 + 1}`} />,
      width: 60,
      disabled: true,
    } as any)
    middleTags[`m${i * 2 + 1}`].setPosition(350, middleTagsPositions[i * 2])

    middleTags[`m${i * 2 + 2}`] = new DuprNodeModel({
      card: <MatchNoCard title={`M${i * 2 + 2}`} />,
      width: 60,
      disabled: true,
    } as any)
    middleTags[`m${i * 2 + 2}`].setPosition(350, middleTagsPositions[i * 2 + 1])

    model.addAll(
      middleWinners[`m${i * 2 + 1}Winner`],
      middleWinners[`m${i * 2 + 2}Winner`],
      middleScores[`m${i * 2 + 1}Score`],
      middleScores[`m${i * 2 + 2}Score`],
      middleTags[`m${i * 2 + 1}`],
      middleTags[`m${i * 2 + 2}`]
    )
  }

  const round3Winners: any = []
  const round3Scores: any = []
  const round3Tags: any = []

  const round3WinnerPositions = [400, 800, 1200, 1600]
  const round3ScoresPositions = [440, 840, 1240, 1640]
  const round3TagsPositions = [404, 804, 1204, 1604]

  const round2EastMatches = round2?.filter((el: any) => el?.tag === 'EAST')

  for (let i = 0; i < 2; i += 1) {
    const match3 = round3MatchSlotExists(i + 1)
    const matchData = getMatchBySlotRound3(i + 1)
    const teamsData = sortBy(matchData?.teams, 'serial')

    round3Winners[`e${i * 2 + 1}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            matchData?.is_team_1_tbd
              ? 'TBD'
              : !match3 || !teamsData?.[0]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    round3Winners[`e${i * 2 + 1}Winner`].setPosition(800, round3WinnerPositions[i * 2])

    round3Winners[`e${i * 2 + 2}Winner`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            matchData?.is_team_2_tbd
              ? 'TBD'
              : !match3 || !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    round3Winners[`e${i * 2 + 2}Winner`].setPosition(800, round3WinnerPositions[i * 2 + 1])

    round3Scores[`e${i * 2 + 1}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          loserInfo={`Loser to N${i + 1}`}
          noOfGames={round2EastMatches?.[i * 2]?.noOfGames}
          bye={round2EastMatches?.[i * 2]?.bye}
          forfeited={round2EastMatches?.[i * 2]?.forfeited}
          score={getScoresArray(round2EastMatches?.[i * 2])}
        />
      ),
      disabled: true,
    } as any)
    round3Scores[`e${i * 2 + 1}Score`].setPosition(800, round3ScoresPositions[i * 2])

    round3Scores[`e${i * 2 + 2}Score`] = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={99}
          loserInfo={`Loser to N${i + 1}`}
          noOfGames={round2EastMatches?.[i * 2 + 1]?.noOfGames}
          bye={round2EastMatches?.[i * 2 + 1]?.bye}
          forfeited={round2EastMatches?.[i * 2 + 1]?.forfeited}
          score={getScoresArray(round2EastMatches?.[i * 2 + 1])}
        />
      ),
      disabled: true,
    } as any)
    round3Scores[`e${i * 2 + 2}Score`].setPosition(800, round3ScoresPositions[i * 2 + 1])

    round3Tags[`e${i * 2 + 1}`] = new DuprNodeModel({
      card: <MatchNoCard title={`E${i * 2 + 1}`} />,
      width: 60,
      disabled: true,
    } as any)
    round3Tags[`e${i * 2 + 1}`].setPosition(700, round3TagsPositions[i * 2])

    round3Tags[`e${i * 2 + 2}`] = new DuprNodeModel({
      card: <MatchNoCard title={`E${i * 2 + 2}`} />,
      width: 60,
      disabled: true,
    } as any)
    round3Tags[`e${i * 2 + 2}`].setPosition(700, round3TagsPositions[i * 2 + 1])
    model.addAll(
      round3Winners[`e${i * 2 + 1}Winner`],
      round3Winners[`e${i * 2 + 2}Winner`],
      round3Scores[`e${i * 2 + 1}Score`],
      round3Scores[`e${i * 2 + 2}Score`],
      round3Tags[`e${i * 2 + 1}`],
      round3Tags[`e${i * 2 + 2}`]
    )
  }

  const round4Winners: any = []
  const round4Scores: any = []
  const round4Tags: any = []

  const round3EastMatches = round3?.filter((el: any) => el.tag === 'EAST')
  for (let i = 0; i < 1; i += 1) {
    const match4 = round4MatchSlotExists(i + 1)
    const matchData = getMatchBySlotRound4(i + 1)
    const teamsData = sortBy(matchData?.teams, 'serial')

    round4Winners.e5Winner = new DuprNodeModel({
      card: (
        <PlayerNameCard
          winner={(matchData?.confirmed || matchData?.forfeited) && !teamsData?.[0]?.winner}
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            matchData?.is_team_1_tbd
              ? 'TBD'
              : !match4 || !teamsData?.[0]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[0]?.player1?.fullName}`
                  : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    round4Winners.e5Winner.setPosition(1150, 600)

    round4Winners.e6Winner = new DuprNodeModel({
      card: (
        <PlayerNameCard
          winner={(matchData?.confirmed || matchData?.forfeited) && !teamsData?.[1]?.winner}
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            matchData?.is_team_2_tbd
              ? 'TBD'
              : !match4 || !teamsData?.[1]?.player1?.fullName
                ? 'N/A'
                : eventFormat === 'SINGLES'
                  ? `${teamsData?.[1]?.player1?.fullName}`
                  : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={0}
        />
      ),
    } as any)
    round4Winners.e6Winner.setPosition(1150, 1400)

    round4Scores.e5Score = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={(matchData?.confirmed || matchData?.forfeited) && !teamsData?.[0]?.winner ? 2 : 99}
          loserInfo="Loser to NE1"
          noOfGames={round3EastMatches?.[i * 2]?.noOfGames}
          bye={round3EastMatches?.[i * 2]?.bye}
          forfeited={round3EastMatches?.[i * 2]?.forfeited}
          score={getScoresArray(round3EastMatches?.[i * 2])}
        />
      ),
      disabled: true,
    } as any)
    round4Scores.e5Score.setPosition(1150, 640)

    round4Scores.e6Score = new DuprNodeModel({
      card: (
        <ScoreDetailsCard
          rank={(matchData?.confirmed || matchData?.forfeited) && !teamsData?.[1]?.winner ? 2 : 99}
          loserInfo="Loser to NE1"
          noOfGames={round3EastMatches?.[i * 2 + 1]?.noOfGames}
          bye={round3EastMatches?.[i * 2 + 1]?.bye}
          forfeited={round3EastMatches?.[i * 2 + 1]?.forfeited}
          score={getScoresArray(round3EastMatches?.[i * 2 + 1])}
        />
      ),
      disabled: true,
    } as any)
    round4Scores.e6Score.setPosition(1150, 1440)

    round4Tags.e5 = new DuprNodeModel({ card: <MatchNoCard title="E5" />, width: 60, disabled: true } as any)
    round4Tags.e5.setPosition(1050, 604)

    round4Tags.e6 = new DuprNodeModel({ card: <MatchNoCard title="E6" />, width: 60, disabled: true } as any)
    round4Tags.e6.setPosition(1050, 1404)
    model.addAll(
      round4Winners.e5Winner,
      round4Winners.e6Winner,
      round4Scores.e5Score,
      round4Scores.e6Score,
      round4Tags.e5,
      round4Tags.e6
    )
  }

  const eastFinalMatch = getMatchBySlotRound4(1)
  const eastRound4Match = round4?.filter((el: any) => el?.tag === 'EAST')?.[0]
  const eastWinner =
    eastFinalMatch?.team1?.winner || eastFinalMatch?.bye ? eastFinalMatch?.team1 : eastFinalMatch?.team2
  const eastWinnerNotPresent = checkIfWinnerNotPresent(eastFinalMatch)

  const e7Winner = new DuprNodeModel({
    card: (
      <PlayerNameCard
        leagueMatchId={eastFinalMatch?.leagueMatchId}
        onClick={() => selectTeamId(eastWinner?.id)}
        winner={!eastWinnerNotPresent}
        title={
          eastWinnerNotPresent
            ? 'TBD'
            : eventFormat === 'SINGLES'
              ? `${eastWinner?.player1?.fullName}`
              : `${eastWinner?.player1?.fullName}, ${eastWinner?.player2?.fullName}`
        }
        rank={1}
      />
    ),
  } as any)
  e7Winner.setPosition(1500, 1000)

  const e7Score = new DuprNodeModel({
    card: (
      <ScoreDetailsCard
        rank={1}
        noOfGames={eastRound4Match?.noOfGames}
        bye={eastRound4Match?.bye}
        forfeited={eastRound4Match?.forfeited}
        score={getScoresArray(eastRound4Match)}
      />
    ),
    disabled: true,
  } as any)
  e7Score.setPosition(1500, 1040)

  const e7 = new DuprNodeModel({ card: <MatchNoCard title="E7" />, width: 60, disabled: true } as any)
  e7.setPosition(1400, 1004)

  const m1WinnerLink = new DefaultLinkModel({ teamId: round2EastMatches?.[0]?.team1?.id })
  m1WinnerLink.setSourcePort(middleWinners.m1Winner.getPort('out'))
  m1WinnerLink.setTargetPort(round3Winners.e1Winner.getPort('in'))

  const m2WinnerLink = new DefaultLinkModel({
    teamId: round2EastMatches?.[0]?.confirmed ? round2EastMatches?.[0]?.team2?.id : 0,
  })
  m2WinnerLink.setSourcePort(middleWinners.m2Winner.getPort('out'))
  m2WinnerLink.setTargetPort(round3Winners.e1Winner.getPort('in'))

  const m3WinnerLink = new DefaultLinkModel({ teamId: round2EastMatches?.[1]?.team1?.id })
  m3WinnerLink.setSourcePort(middleWinners.m3Winner.getPort('out'))
  m3WinnerLink.setTargetPort(round3Winners.e2Winner.getPort('in'))

  const m4WinnerLink = new DefaultLinkModel({ teamId: round2EastMatches?.[1]?.team2?.id })
  m4WinnerLink.setSourcePort(middleWinners.m4Winner.getPort('out'))
  m4WinnerLink.setTargetPort(round3Winners.e2Winner.getPort('in'))

  const m5WinnerLink = new DefaultLinkModel({ teamId: round2EastMatches?.[2]?.team1?.id })
  m5WinnerLink.setSourcePort(middleWinners.m5Winner.getPort('out'))
  m5WinnerLink.setTargetPort(round3Winners.e3Winner.getPort('in'))

  const m6WinnerLink = new DefaultLinkModel({ teamId: round2EastMatches?.[2]?.team2?.id })
  m6WinnerLink.setSourcePort(middleWinners.m6Winner.getPort('out'))
  m6WinnerLink.setTargetPort(round3Winners.e3Winner.getPort('in'))

  const m7WinnerLink = new DefaultLinkModel({ teamId: round2EastMatches?.[3]?.team1?.id })
  m7WinnerLink.setSourcePort(middleWinners.m7Winner.getPort('out'))
  m7WinnerLink.setTargetPort(round3Winners.e4Winner.getPort('in'))

  const m8WinnerLink = new DefaultLinkModel({ teamId: round2EastMatches?.[3]?.team2?.id })
  m8WinnerLink.setSourcePort(middleWinners.m8Winner.getPort('out'))
  m8WinnerLink.setTargetPort(round3Winners.e4Winner.getPort('in'))

  const e5Team = getMatchBySlotRound3(1)
  const e6Team = getMatchBySlotRound3(2)

  const e1WinnerLink = new DefaultLinkModel({ teamId: e5Team?.team1?.id })
  e1WinnerLink.setSourcePort(round3Winners.e1Winner.getPort('out'))
  e1WinnerLink.setTargetPort(round4Winners.e5Winner.getPort('in'))

  const e2WinnerLink = new DefaultLinkModel({ teamId: e5Team?.team2?.id })
  e2WinnerLink.setSourcePort(round3Winners.e2Winner.getPort('out'))
  e2WinnerLink.setTargetPort(round4Winners.e5Winner.getPort('in'))

  const e3WinnerLink = new DefaultLinkModel({ teamId: e6Team?.team1?.id })
  e3WinnerLink.setSourcePort(round3Winners.e3Winner.getPort('out'))
  e3WinnerLink.setTargetPort(round4Winners.e6Winner.getPort('in'))

  const e4WinnerLink = new DefaultLinkModel({ teamId: e5Team?.team2?.id })
  e4WinnerLink.setSourcePort(round3Winners.e4Winner.getPort('out'))
  e4WinnerLink.setTargetPort(round4Winners.e6Winner.getPort('in'))

  const e5WinnerLink = new DefaultLinkModel({ teamId: eastFinalMatch?.team1?.id })
  e5WinnerLink.setSourcePort(round4Winners.e5Winner.getPort('out'))
  e5WinnerLink.setTargetPort(e7Winner.getPort('in'))

  const e6WinnerLink = new DefaultLinkModel({ teamId: eastFinalMatch?.team2?.id })
  e6WinnerLink.setSourcePort(round4Winners.e6Winner.getPort('out'))
  e6WinnerLink.setTargetPort(e7Winner.getPort('in'))

  // 9: Middle Nodes & Links //

  const middle = new DuprNodeModel({
    card: <DirectionCard title="Middle" />,
    width: 300,
    height: 56,
    disabled: true,
  } as any)
  middle.setPosition(100, 144)

  const arr: any = []
  for (let i = 0; i < 8; i += 1) {
    const match1 = getMatchBySlot(i + 1)
    const teamsData = sortBy(match1?.teams, 'serial')

    arr[`player${players[i * 2]}`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[0]?.id)}
          title={
            !round1MatchSlotExists(i + 1)
              ? 'N/A'
              : eventFormat === 'SINGLES'
                ? `${teamsData?.[0]?.player1?.fullName}`
                : `${teamsData?.[0]?.player1?.fullName}, ${teamsData?.[0]?.player2?.fullName}`
          }
          rank={players[i * 2]}
        />
      ),
      direction: `${match1?.bye ? 'right' : teamsData?.[0]?.winner ? 'right' : 'left'}`,
    } as any)
    arr[`player${players[i * 2]}`].setPosition(100, positions[i * 2])

    arr[`player${players[i * 2 + 1]}`] = new DuprNodeModel({
      card: (
        <PlayerNameCard
          onClick={() => selectTeamId(teamsData?.[1]?.id)}
          title={
            !round1MatchSlotExists(i + 1) || match1?.bye
              ? 'N/A'
              : eventFormat === 'SINGLES'
                ? `${teamsData?.[1]?.player1?.fullName}`
                : `${teamsData?.[1]?.player1?.fullName}, ${teamsData?.[1]?.player2?.fullName}`
          }
          rank={players[i * 2 + 1]}
        />
      ),
      direction: `${match1?.bye ? 'left' : teamsData?.[1]?.winner ? 'right' : 'left'}`,
    } as any)
    arr[`player${players[i * 2 + 1]}`].setPosition(100, positions[i * 2 + 1])
    model.addAll(arr[`player${players[i * 2]}`], arr[`player${players[i * 2 + 1]}`])
  }

  const playerWinnerLinks: any = []
  const playerLooserLinks: any = []

  const round1TeamIds: any = []
  for (let i = 0; i < 8; i += 1) {
    round1TeamIds.push(round1?.[i]?.team1?.id, round1?.[i]?.team2?.id)
  }

  for (let i = 0; i < players.length; i += 1) {
    playerWinnerLinks[`player${players[i]}WinnerLink`] = new DefaultLinkModel({ teamId: round1TeamIds?.[i] })
    playerWinnerLinks[`player${players[i]}WinnerLink`].setSourcePort(arr[`player${players[i]}`].getPort('out'))
    playerWinnerLinks[`player${players[i]}WinnerLink`].setTargetPort(
      middleWinners[`m${Math.floor(i / 2) + 1}Winner`].getPort('in')
    )

    playerLooserLinks[`player${players[i]}LooserLink`] = new DefaultLinkModel({
      curvyness: -50,
      teamId: round1TeamIds?.[i],
    })
    playerLooserLinks[`player${players[i]}LooserLink`].setSourcePort(arr[`player${players[i]}`].getPort('out'))
    playerLooserLinks[`player${players[i]}LooserLink`].setTargetPort(
      middleLoosers[`m${Math.floor(i / 2) + 1}Looser`].getPort('in')
    )
  }

  // 10 Winner Cards Nodes //

  const firstWinner = new DuprNodeModel({
    card: (
      <WinnerCard
        rank={1}
        name={
          !canShowStandings
            ? 'TBD'
            : eventFormat === 'SINGLES'
              ? standings?.[0]?.player1?.fullName
              : `${standings?.[0]?.player1?.fullName}, ${standings?.[0]?.player2?.fullName}`
        }
      />
    ),
    disabled: true,
  } as any)
  firstWinner.setPosition(850, 2400)

  const secondWinner = new DuprNodeModel({
    card: (
      <WinnerCard
        rank={2}
        name={
          !canShowStandings
            ? 'TBD'
            : eventFormat === 'SINGLES'
              ? standings?.[1]?.player1?.fullName
              : `${standings?.[1]?.player1?.fullName}, ${standings?.[1]?.player2?.fullName}`
        }
      />
    ),
    disabled: true,
  } as any)
  secondWinner.setPosition(1175, 2400)

  const thirdWinner = new DuprNodeModel({
    card: (
      <WinnerCard
        rank={3}
        name={
          !canShowStandings
            ? 'TBD'
            : eventFormat === 'SINGLES'
              ? standings?.[2]?.player1?.fullName
              : `${standings?.[2]?.player1?.fullName}, ${standings?.[2]?.player2?.fullName}`
        }
      />
    ),
    disabled: true,
  } as any)
  thirdWinner.setPosition(1500, 2400)

  model.addAll(
    northWest,
    north,
    northEast,
    west,
    middle,
    east,
    southWest,
    south,
    southEast,

    ...arr,
    e7Winner,

    w7Winner,

    n3Winner,

    s3Winner,

    ne1Winner,
    nw1Winner,
    se1Winner,
    sw1Winner,

    e7Score,

    w7Score,

    n3Score,
    s3Score,
    ne1Score,
    nw1Score,
    se1Score,
    sw1Score,

    e7,
    w7,
    n3,
    s3,
    ne1,
    nw1,
    se1,
    sw1,

    m1WinnerLink,
    m2WinnerLink,
    m3WinnerLink,
    m4WinnerLink,
    m5WinnerLink,
    m6WinnerLink,
    m7WinnerLink,
    m8WinnerLink,

    m1LooserLink,
    m2LooserLink,
    m3LooserLink,
    m4LooserLink,
    m5LooserLink,
    m6LooserLink,
    m7LooserLink,
    m8LooserLink,

    e1WinnerLink,
    e2WinnerLink,
    e3WinnerLink,
    e4WinnerLink,
    e5WinnerLink,
    e6WinnerLink,

    e1LooserLink,
    e2LooserLink,
    e3LooserLink,
    e4LooserLink,
    e5LooserLink,
    e6LooserLink,

    w1WinnerLink,
    w2WinnerLink,
    w3WinnerLink,
    w4WinnerLink,
    w5WinnerLink,
    w6WinnerLink,

    w1LooserLink,
    w2LooserLink,
    w3LooserLink,
    w4LooserLink,
    w5LooserLink,
    w6LooserLink,

    n1WinnerLink,
    n2WinnerLink,
    n1LooserLink,
    n2LooserLink,

    s1WinnerLink,
    s2WinnerLink,
    s2LooserLink,
    s1LooserLink,

    firstWinner,
    secondWinner,
    thirdWinner
  )

  for (let i = 0; i < 8; i += 1) {
    const match = getMatchBySlot(i + 1)
    if (match?.confirmed || match?.forfeited) {
      const sortedTeams = sortBy(match?.teams, 'serial')
      const team1Winner = sortedTeams?.[0]?.winner
      if (!team1Winner) {
        model.addLink(playerWinnerLinks[`player${players[i * 2 + 1]}WinnerLink`])
        model.addLink(playerLooserLinks[`player${players[i * 2]}LooserLink`])
      } else {
        model.addLink(playerWinnerLinks[`player${players[i * 2]}WinnerLink`])
        model.addLink(playerLooserLinks[`player${players[i * 2 + 1]}LooserLink`])
      }
    }

    if (match?.bye) {
      const sortedTeams = sortBy(match?.teams, 'serial')
      const team1Winner = sortedTeams?.[0]?.winner
      if (team1Winner) {
        model.addLink(playerWinnerLinks[`player${players[i * 2 + 1]}WinnerLink`])
        model.addLink(playerLooserLinks[`player${players[i * 2]}LooserLink`])
      } else {
        model.addLink(playerWinnerLinks[`player${players[i * 2]}WinnerLink`])
        model.addLink(playerLooserLinks[`player${players[i * 2 + 1]}LooserLink`])
      }
    }
  }

  model.setLocked(true)

  const zoomToFit = () => {
    engine.zoomToFitNodes({ margin: 20 })
  }

  const resetZoom = () => {
    model.setZoomLevel(100)
    engine.repaintCanvas()
  }

  const zoomIn = () => {
    model.setZoomLevel(model.getZoomLevel() + zoomStep)
    engine.repaintCanvas()
  }

  const zoomOut = () => {
    const s = model.getZoomLevel() - zoomStep
    if (s <= zoomStep) {
      model.setZoomLevel(zoomStep)
    } else {
      model.setZoomLevel(s)
    }
    engine.repaintCanvas()
  }

  engine.setModel(model)

  engine.registerListener({
    canvasReady: () => engine.zoomToFitNodes({ margin: 20 }),
  })
  return (
    <>
      <div className="relative">
        <div className="absolute top-4 left-4 z-50 flex gap-x-4">
          <Link
            to="../"
            relative="path"
            className="bg-primary hover:bg-primary-700 w-14 h-14 rounded-full flex items-center justify-center"
          >
            <ArrowBackNoBg className="text-white w-6 h-6" />
          </Link>
          <button
            onClick={zoomIn}
            className="bg-primary hover:bg-primary-700 w-14 h-14 rounded-full flex items-center justify-center"
          >
            <ZoomOutMap className="text-white w-6 h-6" />
          </button>
          <button
            onClick={zoomOut}
            className="bg-primary hover:bg-primary-700 w-14 h-14 rounded-full flex items-center justify-center"
          >
            <ZoomInMapIcon className="text-white w-6 h-6" />
          </button>
          <button
            onClick={resetZoom}
            className="bg-primary hover:bg-primary-700 w-14 h-14 rounded-full flex items-center justify-center"
          >
            <CropFree className="text-white w-6 h-6" />
          </button>
          <button
            onClick={zoomToFit}
            className="bg-primary hover:bg-primary-700 w-14 h-14 rounded-full flex items-center justify-center"
          >
            <CenterFocusStrong className="text-white w-6 h-6" />
          </button>
        </div>
        <div className="absolute top-4 right-4 z-50">
          {has(permissions, BRACKET_MODIFY) ? (
            <button className="bg-primary text-white rounded py-2 px-4 text-sm" onClick={downloadPdfFile}>
              Download Diagram
            </button>
          ) : (
            <></>
          )}
          {drawImpacted && bracket?.displayStatus === eventStatus.completed ? (
            <div className="rounded bg-white shadow-lg flex gap-x-2 p-3 max-w-xs">
              <div>
                <ErrorIcon className="text-boldOrange" />
              </div>
              <p className="text-xs text-[#757895]">
                The matches of this event were edited by event director after the completion of the event. Please refer
                to the standings table and matches tab to view the most accurate information regarding the event
                outcome.
              </p>
            </div>
          ) : null}
          {!bracket?.hasConfirmMatch ? (
            <div className="rounded bg-white shadow-lg flex gap-x-2 p-3 max-w-xs">
              <div>
                <ErrorIcon className="text-boldOrange" />
              </div>
              <p className="text-xs text-[#757895]">
                This diagram displays the preliminary seeding created by the event director. The director may reorganize
                this seeding before the event begins.
              </p>
            </div>
          ) : null}
        </div>
        <CanvasWidget className="min-w-screen min-h-screen bg-white" engine={engine} />
      </div>
      {/* <HavingTroubleModal open={openModal} onClose={() => setOpenModal(false)} /> */}
    </>
  )
}
